import React, { useState, useEffect, useCallback } from 'react';
import { Search, Disc, Plus, X, Check, Car, User } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { API } from '@/utils/api';
import axios from 'axios';
import { toast } from 'sonner';
import AtelierLayout from '@/components/atelier/AtelierLayout';
import { TireMoveDialog, MOVES } from '@/components/atelier/TireMoveDialog';

const SEASONS = [
  { value: 'hiver', label: 'Hiver' },
  { value: 'ete', label: 'Été' },
  { value: '4_saisons', label: '4 saisons' },
];
const TIRE_TYPES = [
  { value: 'roues_completes', label: 'Roues complètes' },
  { value: 'pneus_seuls', label: 'Pneus seuls' },
  { value: 'jantes_seules', label: 'Jantes seules' },
];
const STATUS_BADGE = {
  en_stock: { label: 'En stock', cls: 'bg-emerald-100 text-emerald-700' },
  monte_sur_vehicule: { label: 'Monté sur véhicule', cls: 'bg-blue-100 text-blue-700' },
  rendu: { label: 'Rendu au client', cls: 'bg-slate-200 text-slate-700' },
};
const ACTIONS = { en_stock: ['mount', 'return'], monte_sur_vehicule: ['unmount', 'return'], rendu: [] };
const ACTION_LABEL = { montage: 'Déstocké', demontage: 'Entreposé', rendu: 'Rendu' };

const LastMove = ({ s }) => {
  const h = [...(s.history || [])].reverse().find(x => x.by);
  if (!h) return null;
  return <p className="text-[11px] text-slate-500 mt-1" data-testid={`gardiennage-last-move-${s.id}`}>{ACTION_LABEL[h.action] || h.action} par <b>{h.by}</b> le {new Date(h.date).toLocaleString('fr-CH', { dateStyle: 'short', timeStyle: 'short' })}</p>;
};

const AtelierGardiennage = ({ onLogout }) => {
  const [storages, setStorages] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({
    vehicle_id: '', tire_type: 'roues_completes', rim_type: '', tire_brand: '',
    tire_dimension: '', qty_bon: 4, qty_moyen: 0, qty_a_remplacer: 0,
    season: 'hiver', storage_location: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [move, setMove] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/atelier/gardiennage`);
      setStorages(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const searchClients = async (q) => {
    if (q.length < 2) { setClients([]); return; }
    try {
      const res = await axios.get(`${API}/clients`, { params: { search: q } });
      setClients(Array.isArray(res.data) ? res.data.slice(0, 10) : (res.data.clients || []).slice(0, 10));
    } catch { setClients([]); }
  };

  const selectClient = async (client) => {
    setSelectedClient(client);
    setClients([]);
    setClientSearch(`${client.first_name} ${client.last_name}`);
    try {
      const res = await axios.get(`${API}/vehicles`, { params: { client_id: client.id } });
      setVehicles(res.data || []);
    } catch { setVehicles([]); }
  };

  const handleSubmit = async () => {
    if (!selectedClient?.id || !form.vehicle_id) {
      toast.error('Sélectionnez un client et un véhicule');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/atelier/gardiennage/create`, {
        client_id: selectedClient.id,
        ...form,
      });
      toast.success('Gardiennage créé');
      setShowForm(false);
      setSelectedClient(null);
      setClientSearch('');
      setVehicles([]);
      setForm({ vehicle_id: '', tire_type: 'roues_completes', rim_type: '', tire_brand: '', tire_dimension: '', qty_bon: 4, qty_moyen: 0, qty_a_remplacer: 0, season: 'hiver', storage_location: '', notes: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSubmitting(false); }
  };

  const filtered = storages.filter(s => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (s.client_name || '').toLowerCase().includes(q)
      || (s.vehicle_plate || '').toLowerCase().includes(q)
      || (s.tire_brand || '').toLowerCase().includes(q);
  });

  return (
    <AtelierLayout onLogout={onLogout}>
      <div className="p-4 space-y-4" data-testid="atelier-gardiennage-page">
        <div className="flex items-center gap-2 w-full">
          <Disc className="w-5 h-5 text-orange-600 shrink-0" />
          <h1 className="text-lg font-bold text-slate-900 flex-1">Gardiennage</h1>
          <Button size="sm" onClick={() => setShowForm(!showForm)} data-testid="add-gardiennage-btn" className="bg-orange-600 hover:bg-orange-700 shrink-0">
            {showForm ? <><X className="w-4 h-4 mr-1" /> Annuler</> : <><Plus className="w-4 h-4 mr-1" /> Nouveau</>}
          </Button>
        </div>

        {/* ===== CREATION FORM ===== */}
        {showForm && (
          <Card className="p-4 space-y-4 border-orange-200 bg-orange-50/50" data-testid="gardiennage-form">
            <h3 className="text-sm font-bold text-slate-900">Nouveau gardiennage</h3>

            {/* Client search */}
            <div>
              <label className="text-xs font-medium text-slate-600">Client</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={clientSearch}
                  onChange={e => { setClientSearch(e.target.value); searchClients(e.target.value); setSelectedClient(null); }}
                  placeholder="Rechercher un client..."
                  className="pl-9"
                  data-testid="gardiennage-client-search"
                />
              </div>
              {clients.length > 0 && !selectedClient && (
                <div className="mt-1 border rounded-lg bg-white max-h-40 overflow-y-auto shadow-lg" data-testid="client-dropdown">
                  {clients.map(c => (
                    <button key={c.id} onClick={() => selectClient(c)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 border-b last:border-0" data-testid={`client-option-${c.id}`}>
                      <User className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">{c.first_name} {c.last_name}</span>
                      {c.phone && <span className="text-xs text-slate-400 ml-auto">{c.phone}</span>}
                    </button>
                  ))}
                </div>
              )}
              {selectedClient && (
                <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1"><Check className="w-3 h-3" /> {selectedClient.first_name} {selectedClient.last_name}</p>
              )}
            </div>

            {/* Vehicle selection */}
            {selectedClient && (
              <div>
                <label className="text-xs font-medium text-slate-600">Véhicule</label>
                {vehicles.length === 0 ? (
                  <p className="text-xs text-slate-400 mt-1">Aucun véhicule pour ce client</p>
                ) : (
                  <div className="grid gap-2 mt-1">
                    {vehicles.map(v => (
                      <button key={v.id} onClick={() => setForm(f => ({ ...f, vehicle_id: v.id }))}
                        className={`p-3 rounded-lg border text-left text-sm flex items-center gap-2 transition-all ${form.vehicle_id === v.id ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`}
                        data-testid={`vehicle-option-${v.id}`}
                      >
                        <Car className={`w-4 h-4 ${form.vehicle_id === v.id ? 'text-orange-600' : 'text-slate-400'}`} />
                        <span className="font-medium">{v.brand} {v.model}</span>
                        <span className="text-xs text-slate-400 ml-auto">{v.plate}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tire info */}
            {form.vehicle_id && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Type</label>
                    <select value={form.tire_type} onChange={e => setForm(f => ({ ...f, tire_type: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="tire-type-select">
                      {TIRE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Saison</label>
                    <select value={form.season} onChange={e => setForm(f => ({ ...f, season: e.target.value }))}
                      className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="season-select">
                      {SEASONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Marque pneu</label>
                    <Input value={form.tire_brand} onChange={e => setForm(f => ({ ...f, tire_brand: e.target.value }))} placeholder="Michelin..." className="mt-1" data-testid="tire-brand-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Dimension</label>
                    <Input value={form.tire_dimension} onChange={e => setForm(f => ({ ...f, tire_dimension: e.target.value }))} placeholder="225/45R17" className="mt-1" data-testid="tire-dimension-input" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Bon état</label>
                    <Input type="number" value={form.qty_bon} onChange={e => setForm(f => ({ ...f, qty_bon: parseInt(e.target.value) || 0 }))} className="mt-1" data-testid="qty-bon-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Moyen</label>
                    <Input type="number" value={form.qty_moyen} onChange={e => setForm(f => ({ ...f, qty_moyen: parseInt(e.target.value) || 0 }))} className="mt-1" data-testid="qty-moyen-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">A remplacer</label>
                    <Input type="number" value={form.qty_a_remplacer} onChange={e => setForm(f => ({ ...f, qty_a_remplacer: parseInt(e.target.value) || 0 }))} className="mt-1" data-testid="qty-remplacer-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Emplacement</label>
                    <Input value={form.storage_location} onChange={e => setForm(f => ({ ...f, storage_location: e.target.value }))} placeholder="R3-E5" className="mt-1" data-testid="location-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Type de jante</label>
                    <Input value={form.rim_type} onChange={e => setForm(f => ({ ...f, rim_type: e.target.value }))} placeholder="Alu, Tôle..." className="mt-1" data-testid="rim-type-input" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-600">Notes</label>
                  <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Remarques..." className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm min-h-[60px]" data-testid="notes-textarea" />
                </div>
                <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="submit-gardiennage-btn">
                  {submitting ? 'Enregistrement...' : 'Créer le gardiennage'}
                </Button>
              </>
            )}
          </Card>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher par client, plaque, marque..." className="pl-9" data-testid="gardiennage-search" />
        </div>

        {/* List */}
        {loading ? (
          <p className="text-center text-slate-400 py-10">Chargement...</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-slate-400 py-10">Aucun gardiennage trouvé</p>
        ) : (
          <div className="space-y-2" data-testid="gardiennage-list">
            {filtered.map(s => {
              const badge = STATUS_BADGE[s.status] || STATUS_BADGE.en_stock;
              return (
                <Card key={s.id} className="p-4" data-testid={`gardiennage-${s.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
                      <Disc className="w-5 h-5 text-orange-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-slate-900">{s.client_name}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.label}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500 flex-wrap">
                        {s.vehicle_plate && <span className="font-mono">{s.vehicle_plate}</span>}
                        {s.vehicle_info && <span>· {s.vehicle_info}</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-xs flex-wrap">
                        {s.tire_brand && <span className="text-slate-700 font-medium">{s.tire_brand}</span>}
                        {s.tire_dimension && <span className="text-slate-500">{s.tire_dimension}</span>}
                        {s.season && <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${s.season === 'hiver' ? 'bg-blue-100 text-blue-700' : s.season === 'ete' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}`}>{s.season === 'hiver' ? 'Hiver' : s.season === 'ete' ? 'Été' : '4 saisons'}</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400">
                        <span>Bon: {s.qty_bon || 0}</span>
                        <span>Moyen: {s.qty_moyen || 0}</span>
                        <span>A remplacer: {s.qty_a_remplacer || 0}</span>
                        {s.storage_location && <span>| Empl: {s.storage_location}</span>}
                      </div>
                      <LastMove s={s} />
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      {(ACTIONS[s.status] || []).map(m => (
                        <Button key={m} size="sm" className={MOVES[m].btn} onClick={() => setMove({ move: m, storage: s })} data-testid={`gardiennage-${m}-btn-${s.id}`}>{MOVES[m].label}</Button>
                      ))}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      <TireMoveDialog move={move?.move} storage={move?.storage} onClose={() => setMove(null)} onDone={() => { setMove(null); loadData(); }} />
    </AtelierLayout>
  );
};

export default AtelierGardiennage;
